import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Field, reduxForm, change, SubmissionError, submit } from "redux-form";
import { Blade, Button, ProgressIndicator, ReduxControls } from "rhombus-react";

import useActiveCreative from "hooks/useActiveCreative";

import { renameConcept, fetchConceptByNameAndType } from "reducers/Concepts";
import { closeModal } from 'reducers/Modal';
import { loadConcept } from "reducers/ConceptDetails";
import {
	conceptNameAllowedChars,
	conceptNameMaxLength,
	CONCEPT_NAME_MAX_LENGTH,
	OPEN_CONCEPT_NAME_MAX_LENGTH,
	openConceptNameMaxLength
} from 'widgets/forms/validators/Concepts';
import { HTML_CONCEPT_TYPE, SOCIAL_CONCEPT_TYPE } from 'utils/constants';

import "./RenameConcepts.css";

const RENAME_CONCEPTS_FORM = "rename-creatives-form";

function RenameConcepts(props){
	let dispatch = useDispatch()
		, [isLoading, setIsLoading] = useState(false)
		, {
			conceptId,
			conceptName,
			libraryId,
			handleSubmit,
			submitting,
			creativeId,
			invalid,
		} = props
		, { concept, conceptType } = useSelector(state => ({
			concept: state.conceptDetails,
			conceptType: state.conceptDetails?.data?.conceptType,
		}))
		, onClose = () => dispatch(closeModal())
		;

	useActiveCreative(creativeId);

	let charMax = CONCEPT_NAME_MAX_LENGTH;
	let lengthValidator = conceptNameMaxLength;

	if(conceptType === HTML_CONCEPT_TYPE){
		charMax = OPEN_CONCEPT_NAME_MAX_LENGTH;
		lengthValidator = openConceptNameMaxLength;
	}

	useEffect(() => {
		setIsLoading(true);
		dispatch(loadConcept(libraryId, conceptId)).then(() => setIsLoading(false));
		dispatch(change(RENAME_CONCEPTS_FORM, "conceptName", conceptName));
		dispatch(change(RENAME_CONCEPTS_FORM, "conceptId", conceptId));
		dispatch(change(RENAME_CONCEPTS_FORM, "libraryId", libraryId));
	}, [libraryId, conceptId, conceptName]);

	useEffect(() => {
		dispatch(change(RENAME_CONCEPTS_FORM, "conceptType", conceptType));
	}, [conceptType]);

	const renderContent = () => (
		<form className="rename-creative-form" onSubmit={handleSubmit(submitFunction)}>
			<ProgressIndicator isLoading={isLoading || submitting} />
			<Field
				name="conceptName"
				label="New Name for Concept"
				component={ReduxControls.Input}
				placeholder={conceptName}
				charMax={charMax}
				autoFocus
				showCharCount
				required
				validate={[lengthValidator, conceptNameAllowedChars]}
			/>
		</form>
	);

	const renderFooter = () => ([
		<Button key="cancel" theme="grey" disabled={submitting} onClick={onClose}>Cancel</Button>,
		<Button
			key="submit"
			theme="green"
			disabled={submitting || isLoading || invalid}
			onClick={() => dispatch(submit(RENAME_CONCEPTS_FORM))}
		>
			Rename Concept
		</Button>
	]);

	return (
		<Blade
			type="modal"
			setAppTitle={false}
			className="rename-concepts"
			title={!!concept?.name ? `Rename Concept - ${concept.name}` : 'Rename Concept'}
			allowFullScreen={false}
			xClose
			bladeProps={{ verticallyCenter: true }}
			onClose={onClose}
			renderContent={renderContent}
			renderFooter={renderFooter}
		/>
	);
}

RenameConcepts.displayName = "RenameConcepts";

function submitFunction(values, dispatch) {
	let { libraryId, conceptName, conceptType, conceptId } = values || {};
	let conceptTypeForSubmit = conceptType;
	if (conceptType === "social"){
		//If `conceptType` is "social", it has to be changed to the hard-coded server value for the advanced filter to find it.
		conceptTypeForSubmit = SOCIAL_CONCEPT_TYPE;
	}
	return dispatch(fetchConceptByNameAndType(libraryId, conceptName, conceptTypeForSubmit))
		.then((existingConcept) => {
			if (!existingConcept) {
				return dispatch(renameConcept({ conceptId, libraryId, newFileName: conceptName, conceptTypeForSubmit }))
					.then((response) => {
						if (response?.success) {
							return dispatch(closeModal());
						}
					});
			} else {
				throw new SubmissionError({ conceptName: 'A concept with that name already exists in this creative library' });
			}
		});
}

export default reduxForm({
	form: RENAME_CONCEPTS_FORM,
	onSubmit: submitFunction,
})(RenameConcepts);
